import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../Layout/DashboardLayout";
import DashboardTopbar from "../../Layout/DashboardTopbar";
import useTokenBalance from "../../hooks/useTokenBalance";
import { useAuthContext } from "../../Context/AuthContext";
import { useAlertContext } from "../../Context/AlertContext";
import {
  downloadInvoiceFile,
  fetchInvoiceListForCurrentUser
} from "../../utils/invoiceApi";
import "./invoices.css";

const Invoices = () => {
  const { t } = useTranslation("common");
  const { user } = useAuthContext();
  const { setError } = useAlertContext();
  const { tokenBalance } = useTokenBalance();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchInvoiceListForCurrentUser();
      setInvoices(list);
    } catch (error) {
      console.error("Failed to load invoices:", error);
      setInvoices([]);
      setError(error.message || t("Unable to load invoices. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [setError, t]);

  useEffect(() => {
    if (!user) return undefined;
    loadInvoices();
    return undefined;
  }, [user, loadInvoices]);

  const handleDownload = async (filename, folderpath) => {
    console.log("select file for download", filename, folderpath);
    const downloadKey = `${folderpath}::${filename}`;
    if (downloadingKey) return;

    setDownloadingKey(downloadKey);
    try {
      const response = await downloadInvoiceFile(filename, folderpath);
      console.log("Download response:", response);
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(error.message || t("Unable to download invoice. Please try again."));
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="dashboard-page invoices-page">
        <DashboardTopbar title={t("Invoices")} tokenBalance={tokenBalance} />

        <div className="page-content">
          <div className="page-header">
            <h1>{t("Invoices")}</h1>
            <p>{t("View and download your billing invoices.")}</p>
          </div>

          <div className="card invoices-card">
            {loading ? (
              <div className="dashboard-loading">{t("Fetching...")}</div>
            ) : invoices.length === 0 ? (
              <div className="dashboard-empty">{t("No invoices found.")}</div>
            ) : (
              <div className="table-scroll">
                <table className="data-table invoices-table">
                  <thead>
                    <tr>
                      <th>{t("Invoice Name")}</th>
                      <th>{t("Created Date")}</th>
                      <th className="invoices-action-col">{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => {
                      const downloadKey = `${invoice.filepath}::${invoice.filename}`;
                      const isDownloading = downloadingKey === downloadKey;
                      return (
                        <tr key={downloadKey}>
                          <td className="invoice-name-cell">{invoice.filename}</td>
                          <td>{invoice.lastmodified || "—"}</td>
                          <td className="invoices-action-col">
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              disabled={Boolean(downloadingKey)}
                              onClick={() =>
                                handleDownload(invoice.filename, invoice.filepath)
                              }>
                              {isDownloading ? t("Downloading...") : t("Download")}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Invoices;
